import React, { FC, useCallback, memo } from "react";
import { shallowEqual } from "react-redux";
import { useAppDispatch, useAppSelector } from "stores";
import store from "stores/interfaces";
import { Divider, Menu, MenuItem, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import BackspaceIcon from "@mui/icons-material/Backspace";
import { addUndo } from "stores/modules/entities.room.histories/slice";
import { getRoomMarkerById } from "stores/modules/entities.rooms/selectors";

const MarkerMenu: FC = () => {
  const dispatch = useAppDispatch();
  const [t] = useTranslation();
  const [open, top, left, markerId, roomId] = useAppSelector((state) => {
    const markerId = store.getAppState(state, "openRoomMarkerMenuId") ?? "";
    const roomId = store.getAppState(state, "roomId") ?? "";
    return [
      store.getAppState(state, "openRoomMarkerMenu"),
      store.getAppState(state, "roomPointerY"),
      store.getAppState(state, "roomPointerX"),
      markerId,
      roomId,
    ] as const;
  }, shallowEqual);
  const marker = useAppSelector((state) =>
    getRoomMarkerById(state, roomId, markerId)
  );
  const isLocked = marker?.locked ?? false;
  const onClose = useCallback(() => {
    dispatch(
      store.appStateMutate((state) => {
        state.openRoomMarkerMenu = false;
      })
    );
  }, [dispatch]);
  const onDeleteMarker = useCallback(() => {
    if (window.confirm(t("本当に削除しますか？"))) {
      if (markerId == null) return;
      dispatch(store.deleteRoomMarker(roomId, markerId));
      dispatch(
        addUndo({
          kind: "update-marker",
          id: markerId,
          before: marker,
          after: null,
        })
      );
      onClose();
    }
  }, [markerId, roomId, onClose, dispatch, t, marker]);
  const onEditMarker = useCallback(() => {
    dispatch(
      store.appStateMutate((state) => {
        state.openRoomMarkerDetail = true;
        state.openRoomMarkerDetailId = markerId;
      })
    );
    onClose();
  }, [markerId, onClose, dispatch]);
  const onCloneMarker = useCallback(() => {
    dispatch(store.cloneRoomMarker(markerId));
    onClose();
  }, [dispatch, markerId, onClose]);
  const onCopyMarkerToClipboard = useCallback(() => {
    dispatch(store.copyRoomMarkerToClipboard(markerId));
    onClose();
  }, [dispatch, markerId, onClose]);
  const onLockMarker = useCallback(() => {
    if (markerId == null) return;
    dispatch(store.addUndoUpdateMarker(roomId, markerId, { locked: true }));
    onClose();
  }, [markerId, roomId, onClose, dispatch]);
  const onUnlockMarker = useCallback(() => {
    if (markerId == null) return;
    dispatch(store.addUndoUpdateMarker(roomId, markerId, { locked: false }));
    onClose();
  }, [markerId, roomId, onClose, dispatch]);
  const onRotateMarker = useCallback(() => {
    if (markerId == null || marker == null) return;
    dispatch(
      store.addUndoUpdateMarker(roomId, markerId, {
        angle: (marker.angle ?? 0) + 45,
      })
    );
    onClose();
  }, [markerId, roomId, marker, onClose, dispatch]);

  return (
    <MemoMarkerMenuInternal
      open={open}
      top={top}
      left={left}
      isLocked={isLocked}
      onClose={onClose}
      onDeleteMarker={onDeleteMarker}
      onEditMarker={onEditMarker}
      onCloneMarker={onCloneMarker}
      onCopyMarkerToClipboard={onCopyMarkerToClipboard}
      onLockMarker={onLockMarker}
      onUnlockMarker={onUnlockMarker}
      onRotateMarker={onRotateMarker}
    />
  );
};

type MarkerMenuInternalProps = {
  open: boolean;
  top: number;
  left: number;
  isLocked: boolean;
  onClose: () => void;
  onDeleteMarker: () => void;
  onEditMarker: () => void;
  onCloneMarker: () => void;
  onCopyMarkerToClipboard: () => void;
  onLockMarker: () => void;
  onUnlockMarker: () => void;
  onRotateMarker: () => void;
};

const MarkerMenuInternal: React.FC<MarkerMenuInternalProps> = (props) => {
  const {
    open,
    left,
    top,
    isLocked,
    onClose,
    onDeleteMarker,
    onEditMarker,
    onCloneMarker,
    onCopyMarkerToClipboard,
    onLockMarker,
    onUnlockMarker,
    onRotateMarker,
  } = props;
  const [t] = useTranslation();

  return (
    <Menu
      open={open}
      anchorReference="anchorPosition"
      anchorPosition={{ left, top }}
      onClose={onClose}
      MenuListProps={{ dense: true }}
      PaperProps={{
        style: {
          width: "20ch",
        },
      }}
      disableRestoreFocus
    >
      <MenuItem onClick={onEditMarker}>{t("編集")}</MenuItem>
      {isLocked ? (
        <MenuItem onClick={onUnlockMarker}>
          {t("配置固定を解除")}
          <ShortcutKeyInfo
            color="textSecondary"
            variant="caption"
            align="right"
          >
            L
          </ShortcutKeyInfo>
        </MenuItem>
      ) : (
        <MenuItem onClick={onLockMarker}>
          {t("配置固定")}
          <ShortcutKeyInfo
            color="textSecondary"
            variant="caption"
            align="right"
          >
            L
          </ShortcutKeyInfo>
        </MenuItem>
      )}
      <Divider style={{ marginTop: 8, marginBottom: 8 }} />
      <MenuItem onClick={onRotateMarker}>
        {t("回転する")}
        <ShortcutKeyInfo color="textSecondary" variant="caption" align="right">
          R or Shift+R
        </ShortcutKeyInfo>
      </MenuItem>
      <Divider style={{ marginTop: 8, marginBottom: 8 }} />
      <MenuItem onClick={onCloneMarker}>
        {t("複製")}
        <ShortcutKeyInfo color="textSecondary" variant="caption" align="right">
          Ctrl+D
        </ShortcutKeyInfo>
      </MenuItem>
      <MenuItem onClick={onCopyMarkerToClipboard}>
        {t("コピー")}
        <ShortcutKeyInfo color="textSecondary" variant="caption" align="right">
          Ctrl+C
        </ShortcutKeyInfo>
      </MenuItem>
      <Divider style={{ marginTop: 8, marginBottom: 8 }} />
      <MenuItem onClick={onDeleteMarker}>
        {t("削除")}
        <ShortcutKeyInfo color="textSecondary" variant="caption" align="right">
          Ctrl+
          <BackspaceIcon
            fontSize="inherit"
            style={{ verticalAlign: "middle" }}
          />
        </ShortcutKeyInfo>
      </MenuItem>
    </Menu>
  );
};

const ShortcutKeyInfo = styled(Typography)`
  flex: 1;
`;

const MemoMarkerMenuInternal = memo(MarkerMenuInternal);

export default memo(MarkerMenu);
